import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, X, ArrowLeft, Trophy, BarChart2, Volume2 } from "lucide-react";
import { API_BASE } from "../config";
import "../styles/vocabTestGame.css";
import "../styles/wordRelationsGame.css";
import apiFetch from "../apiFetch";

const TYPE_LABELS = {
  definitions: "Definitions Test",
  synonyms:    "Synonyms Test",
  antonyms:    "Antonyms Test",
  mixed:       "Mixed Test",
};

/* ── Shuffle helper ── */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const isPlaceholder = (def) => def && def.includes("not pre-seeded");

/* ── Build one MCQ question from a word and the full word pool ── */
function buildQuestion(word, allWords, testType) {
  // For mixed, pick a sub-type weighted by availability
  let subType = testType;
  if (testType === "mixed") {
    const hasSyn = word.synonyms && word.synonyms.length >= 3;
    const hasAnt = word.antonyms && word.antonyms.length >= 1;
    const choices = ["definitions"];
    if (hasSyn) choices.push("synonyms");
    if (hasAnt) choices.push("antonyms");
    subType = choices[Math.floor(Math.random() * choices.length)];
  }

  if (subType === "synonyms") {
    const correct = word.synonyms[Math.floor(Math.random() * word.synonyms.length)];
    // Distractors: other synonyms from other words
    const distractors = allWords
      .filter((w) => w.id !== word.id && w.synonyms && w.synonyms.length > 0)
      .sort(() => Math.random() - 0.5)
      .slice(0, 10)
      .map((w) => w.synonyms[Math.floor(Math.random() * w.synonyms.length)])
      .filter((s) => s !== correct);
    const options = shuffle([correct, ...distractors.slice(0, 3)]);
    return {
      word: word.word,
      definition: word.definition,
      tier: word.tier,
      prompt: `Which word is a synonym of "${word.word}"?`,
      subType: "synonyms",
      options,
      correct,
    };
  }

  if (subType === "antonyms") {
    const validAnt = word.antonyms.filter((a) => a && a.trim());
    const correct = validAnt[Math.floor(Math.random() * validAnt.length)];
    const distractors = allWords
      .filter((w) => w.id !== word.id && w.antonyms && w.antonyms.some((a) => a && a.trim()))
      .sort(() => Math.random() - 0.5)
      .slice(0, 10)
      .map((w) => {
        const valid = w.antonyms.filter((a) => a && a.trim());
        return valid[Math.floor(Math.random() * valid.length)];
      })
      .filter((s) => s && s !== correct);
    const options = shuffle([correct, ...distractors.slice(0, 3)]);
    return {
      word: word.word,
      definition: word.definition,
      tier: word.tier,
      prompt: `Which word is an antonym of "${word.word}"?`,
      subType: "antonyms",
      options,
      correct,
    };
  }

  // definitions: show the word, pick its definition
  const correct = word.definition;
  const distractors = allWords
    .filter((w) => w.id !== word.id && !isPlaceholder(w.definition))
    .sort(() => Math.random() - 0.5)
    .slice(0, 6)
    .map((w) => w.definition)
    .filter((d) => d !== correct);
  const options = shuffle([correct, ...distractors.slice(0, 3)]);
  return {
    word: word.word,
    definition: word.definition,
    tier: word.tier,
    prompt: `What is the definition of "${word.word}"?`,
    subType: "definitions",
    options,
    correct,
  };
}

export default function VocabTestGame() {
  const navigate = useNavigate();
  const location = useLocation();
  const { testType = "definitions", wordCount = 10, startTier = 1, endTier = null } = location.state || {};

  const userId = parseInt(localStorage.getItem("userId"), 10);

  useEffect(() => { document.title = `${TYPE_LABELS[testType] || "Vocabulary Test"} — Owllet Reading Quest`; }, [testType]);

  const [gameState, setGameState] = useState("loading"); // loading | playing | feedback | finished
  const [words, setWords]         = useState([]);
  const [allWords, setAllWords]   = useState([]);
  const [questions, setQuestions] = useState([]);
  const [index, setIndex]         = useState(0);
  const [selected, setSelected]   = useState(null);
  const [answers, setAnswers]     = useState([]); // [{ wordId, correct }]
  const [score, setScore]         = useState(0);
  const [speaking, setSpeaking]   = useState(false);

  useEffect(() => () => window.speechSynthesis.cancel(), []);

  const speak = useCallback((q) => {
    window.speechSynthesis.cancel();
    setSpeaking(true);

    const utterances = [
      q.prompt,
      ...(q.subType !== "definitions" ? [`It means, ${q.definition}.`] : []),
      ...q.options.map((opt, i) => `Option ${i + 1}. ${opt}.`),
    ];

    let idx = 0;
    const speakNext = () => {
      if (idx >= utterances.length) { setSpeaking(false); return; }
      const utt = new SpeechSynthesisUtterance(utterances[idx++]);
      utt.rate = 0.78;
      utt.onend   = speakNext;
      utt.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utt);
    };
    speakNext();
  }, []);

  /* ── Load words ── */
  useEffect(() => {
    if (!userId) { navigate("/projLoginPage"); return; }

    Promise.all([
      apiFetch(`/api/vocab-test/words?userId=${userId}&count=${wordCount}&type=${testType}&startTier=${startTier}${endTier != null ? `&endTier=${endTier}` : ""}`).then((r) => r.json()),
      apiFetch(`/api/vocabulary`).then((r) => r.json()),
    ])
      .then(([testData, vocabData]) => {
        const selected = testData.words || [];
        const pool = vocabData.words || [];
        if (selected.length === 0) { setGameState("error"); return; }
        setWords(selected);
        setAllWords(pool);
        const qs = selected.map((w) => buildQuestion(w, pool, testType));
        setQuestions(qs);
        setGameState("playing");
      })
      .catch(() => setGameState("error"));
  }, [userId, wordCount, testType, startTier, endTier, navigate]);

  const current = questions[index];

  const handleSelect = useCallback((opt) => {
    if (gameState !== "playing") return;
    setSelected(opt);
    setGameState("feedback");
  }, [gameState]);

  const handleNext = useCallback(() => {
    const isCorrect = selected === current.correct;
    const newAnswers = [...answers, { wordId: words[index].id, correct: isCorrect }];
    const newScore = score + (isCorrect ? 1 : 0);

    setAnswers(newAnswers);
    setScore(newScore);

    if (index + 1 >= questions.length) {
      const coinsEarned = Math.max(1, Math.round((newScore / questions.length) * 20));
      apiFetch(`/api/vocab-test/save-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          testType,
          wordCount: questions.length,
          score: newScore,
          maxScore: questions.length,
          answers: newAnswers,
        }),
      }).catch(() => {});
      apiFetch(`/api/resources/update-coins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, delta: coinsEarned }),
      }).catch(() => {});
      apiFetch(`/api/resources/xp/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, delta: coinsEarned * 5 }),
      }).catch(() => {});
      setAnswers(newAnswers);
      setScore(newScore);
      setGameState("finished");
    } else {
      setIndex(index + 1);
      setSelected(null);
      setGameState("playing");
    }
  }, [selected, current, answers, score, index, questions, words, userId, testType]);

  /* ── Keyboard shortcuts ── */
  useEffect(() => {
    const KEYS = ["1", "2", "3", "4"];
    const handler = (e) => {
      if (gameState === "playing" && current) {
        const i = KEYS.indexOf(e.key);
        if (i >= 0 && i < current.options.length) handleSelect(current.options[i]);
      }
      if (gameState === "feedback" && e.key === "Enter") handleNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [gameState, current, handleSelect, handleNext]);

  /* ── States ── */
  if (gameState === "loading") {
    return (
      <div className="vtg-page vtg-center">
        <div className="vtg-spinner" aria-hidden="true" />
        <p role="status" aria-live="polite">Loading questions…</p>
      </div>
    );
  }

  if (gameState === "error") {
    return (
      <div className="vtg-page vtg-center">
        <p className="vtg-error">Could not load words for the selected options.</p>
        <button className="vtg-btn-secondary" onClick={() => navigate("/vocab-test/setup", { state: { testType } })}>
          Back to Setup
        </button>
      </div>
    );
  }

  if (gameState === "finished") {
    const pct = Math.round((score / questions.length) * 100);
    const grade = pct >= 80 ? "Excellent" : pct >= 60 ? "Good" : pct >= 40 ? "Fair" : "Keep practising";
    const gradeClass = pct >= 80 ? "vtg-grade-great" : pct >= 60 ? "vtg-grade-good" : pct >= 40 ? "vtg-grade-mid" : "vtg-grade-low";

    return (
      <div className="vtg-page vtg-center">
        <div className="vtg-results-card">
          <Trophy size={40} className="vtg-trophy" />
          <h2>{TYPE_LABELS[testType]}</h2>
          <p className="vtg-results-sub">Test complete!</p>

          <div className="vtg-score-big">
            <span className={gradeClass}>{score}/{questions.length}</span>
            <span className="vtg-pct">({pct}%)</span>
          </div>
          <p className={`vtg-grade-label ${gradeClass}`}>{grade}</p>

          {/* Per-question breakdown */}
          <div className="vtg-breakdown">
            {questions.map((q, i) => {
              const ans = answers[i];
              return (
                <div key={i} className={`vtg-bk-row ${ans?.correct ? "vtg-bk-correct" : "vtg-bk-wrong"}`}>
                  <span className="vtg-bk-icon">{ans?.correct ? <Check size={13} /> : <X size={13} />}</span>
                  <span className="vtg-bk-word">{q.word}</span>
                  <span className="vtg-bk-tier">T{q.tier}</span>
                </div>
              );
            })}
          </div>

          <div className="vtg-results-actions">
            <button className="vtg-btn-secondary" onClick={() => navigate("/vocab-test/setup", { state: { testType } })}>
              Try Again
            </button>
            <button className="vtg-btn-secondary" onClick={() => navigate("/test-modes")}>
              <BarChart2 size={15} /> All Tests
            </button>
            <button className="vtg-btn-primary" onClick={() => navigate("/home")}>
              Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Playing / feedback ── */
  const progress = ((index) / questions.length) * 100;
  const isFeedback = gameState === "feedback";
  const isCorrect = selected === current?.correct;

  return (
    <div className="vtg-page">
      {/* Header */}
      <div className="vtg-header">
        <button className="vtg-header-back" onClick={() => navigate("/test-modes")}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className="vtg-header-center">
          <span className="vtg-header-title">{TYPE_LABELS[testType]}</span>
          <span className="vtg-header-prog">{index + 1} / {questions.length}</span>
        </div>
        <div className="vtg-header-score">
          <Check size={14} className="vtg-check-icon" /> {score}
        </div>
      </div>

      {/* Progress bar */}
      <div className="vtg-progress-track">
        <div className="vtg-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Question card */}
      <div className="vtg-body">
        <div className="vtg-card">
          {/* Tier badge */}
          <span className="vtg-tier-badge">Tier {current.tier}</span>

          {/* Sub-type label */}
          <p className="vtg-sub-type">{current.subType === "synonyms" ? "Synonym" : current.subType === "antonyms" ? "Antonym" : "Definition"}</p>

          {/* Prompt + speak */}
          <div className="vtg-prompt-row">
            <h2 className="vtg-prompt">{current.prompt}</h2>
            <button
              className={`wr-speak-btn ${speaking ? "speaking" : ""}`}
              onClick={() => speak(current)}
              aria-label={speaking ? "Reading question aloud" : "Read question aloud"}
              aria-pressed={speaking}
            >
              <Volume2 size={18} />
            </button>
          </div>

          {/* Hint: show definition for synonym/antonym questions */}
          {(current.subType === "synonyms" || current.subType === "antonyms") && (
            <p className="vtg-definition-hint">"{current.definition}"</p>
          )}

          {/* Options */}
          <div className="vtg-options">
            {current.options.map((opt, i) => {
              let cls = "vtg-option";
              if (isFeedback) {
                if (opt === current.correct) cls += " vtg-option-correct";
                else if (opt === selected)   cls += " vtg-option-wrong";
              } else if (opt === selected) {
                cls += " vtg-option-selected";
              }
              return (
                <button
                  key={i}
                  className={cls}
                  onClick={() => handleSelect(opt)}
                  disabled={isFeedback}
                >
                  <span className="vtg-option-key">{i + 1}</span>
                  <span className="vtg-option-text">{opt}</span>
                  {isFeedback && opt === current.correct && <Check size={16} className="vtg-opt-icon" />}
                  {isFeedback && opt === selected && opt !== current.correct && <X size={16} className="vtg-opt-icon" />}
                </button>
              );
            })}
          </div>

          {/* Feedback banner */}
          <div aria-live="polite" aria-atomic="true">
            {isFeedback && (
              <div className={`vtg-feedback ${isCorrect ? "vtg-fb-correct" : "vtg-fb-wrong"}`}>
                <span>{isCorrect ? "Correct!" : `Incorrect. Correct answer: ${current.correct}`}</span>
                <button className="vtg-next-btn" onClick={handleNext}>
                  {index + 1 < questions.length ? "Next →" : "Finish"}
                </button>
              </div>
            )}
          </div>
        </div>

        <p className="vtg-key-hint">Press 1–4 to answer · Enter to continue</p>
      </div>
    </div>
  );
}
